import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { Breadcrumbs } from '../../AbstractElements'
import { getIdData, addData, updateData } from '../../lib/collegeList2'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

const emptyForm = () => ({
	class: '',
	college_name: '',
	state: '',
	city: '',
	ranking: '',
	gpa: '',
	acceptance: '',
	'0_10': '',
	'1_11': '',
	'26_50': '',
	'51_75': '',
	'76_100': '',
	low_sat: '',
	high_sat: '',
	low_act: '',
	high_act: '',
	tuition: '',
	undergraduate_enrollment: ''
})

function toNullableInt(v) {
	if (v === '' || v === null || v === undefined) return null
	const n = parseInt(String(v), 10)
	return Number.isNaN(n) ? null : n
}

function toNullableFloat(v) {
	if (v === '' || v === null || v === undefined) return null
	const n = parseFloat(String(v))
	return Number.isNaN(n) ? null : n
}

function rowToForm(row) {
	const f = emptyForm()
	if (!row) return f
	Object.keys(f).forEach((k) => {
		const val = row[k]
		f[k] = val === null || val === undefined ? '' : String(val)
	})
	return f
}

function formToPayload(form, id) {
	const payload = {
		class: form.class.trim(),
		college_name: form.college_name.trim(),
		state: form.state.trim() || null,
		city: form.city.trim() || null,
		ranking: toNullableInt(form.ranking),
		gpa: toNullableFloat(form.gpa),
		acceptance: toNullableInt(form.acceptance),
		'0_10': toNullableInt(form['0_10']),
		'1_11': toNullableInt(form['1_11']),
		'26_50': toNullableInt(form['26_50']),
		'51_75': toNullableInt(form['51_75']),
		'76_100': toNullableInt(form['76_100']),
		low_sat: toNullableInt(form.low_sat),
		high_sat: toNullableInt(form.high_sat),
		low_act: toNullableInt(form.low_act),
		high_act: toNullableInt(form.high_act),
		tuition: toNullableInt(form.tuition),
		undergraduate_enrollment: toNullableInt(form.undergraduate_enrollment)
	}
	if (id) payload.id = id
	return payload
}

function NumField({ name, label, value, onChange, disabled, inputStyle, labelStyle }) {
	return (
		<Col md="6" lg="4">
			<FormGroup>
				<Label style={labelStyle}>{label}</Label>
				<Input
					type="text"
					inputMode="numeric"
					value={value}
					onChange={(e) => onChange(name, e.target.value)}
					disabled={disabled}
					style={inputStyle}
				/>
			</FormGroup>
		</Col>
	)
}

const AdminCollegeEdit2 = () => {
	const navigate = useNavigate()
	const { id, layout } = useParams()
	const role = useRoleBasedUrl()
	const isAddMode = !id || id === 'undefined'

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [form, setForm] = useState(emptyForm)
	const [isDarkMode, setIsDarkMode] = useState(false)

	useEffect(() => {
		const checkDarkMode = () => setIsDarkMode(document.body.classList.contains('dark-only'))
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (isAddMode) return
		let cancelled = false
		;(async () => {
			try {
				setLoading(true)
				const rows = await getIdData(id)
				if (cancelled) return
				if (rows?.length) setForm(rowToForm(rows[0]))
			} catch (err) {
				console.error(err)
				setError('Failed to load record')
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [id, isAddMode])

	const setField = useCallback((name, value) => {
		setForm((prev) => ({ ...prev, [name]: value }))
	}, [])

	const handleSubmit = async (e) => {
		e.preventDefault()
		setError(null)
		if (!form.class.trim() || !form.college_name.trim()) {
			setError('Class and College name are required.')
			return
		}
		setLoading(true)
		try {
			if (isAddMode) {
				await addData(formToPayload(form))
			} else {
				await updateData(formToPayload(form, Number(id)))
			}
			navigate(`${process.env.PUBLIC_URL}/${role}/college-list-2/${layout}`)
		} catch (err) {
			console.error(err)
			setError(err.message || 'Save failed')
		} finally {
			setLoading(false)
		}
	}

	const cancel = () => navigate(`${process.env.PUBLIC_URL}/${role}/college-list-2/${layout}`)

	const inputStyle = {
		backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
		color: isDarkMode ? '#fff' : 'inherit'
	}
	const labelStyle = { color: isDarkMode ? '#fff' : 'inherit' }

	if (loading && !isAddMode && !form.college_name && !error) {
		return (
			<Container fluid>
				<Breadcrumbs parent="College Management" title={isAddMode ? 'Add' : 'Edit'} mainTitle="College Management" />
				<div className="text-center p-5">
					<div className="spinner-border text-primary" role="status" />
				</div>
			</Container>
		)
	}

	return (
		<Container fluid>
			<Breadcrumbs parent="College Management" title={isAddMode ? 'Add college' : 'Edit college'} mainTitle="College Management" />
			<Row>
				<Col sm="12">
					<Card style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
						<CardBody>
							<h4 className="mb-3" style={labelStyle}>
								{isAddMode ? 'Add college (collegeList2)' : 'Edit college'}
							</h4>
							{error && <Alert color="danger">{error}</Alert>}
							<form onSubmit={handleSubmit}>
								<h6 className="text-muted mb-2">Basics</h6>
								<Row>
									<Col md="6">
										<FormGroup>
											<Label style={labelStyle}>Class *</Label>
											<Input value={form.class} onChange={(e) => setField('class', e.target.value)} required disabled={loading} style={inputStyle} />
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<Label style={labelStyle}>College name *</Label>
											<Input value={form.college_name} onChange={(e) => setField('college_name', e.target.value)} required disabled={loading} style={inputStyle} />
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<Label style={labelStyle}>State</Label>
											<Input value={form.state} onChange={(e) => setField('state', e.target.value)} disabled={loading} style={inputStyle} />
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<Label style={labelStyle}>City</Label>
											<Input value={form.city} onChange={(e) => setField('city', e.target.value)} disabled={loading} style={inputStyle} />
										</FormGroup>
									</Col>
								</Row>

								<h6 className="text-muted mb-2 mt-3">Rank &amp; stats</h6>
								<Row>
									<NumField name="ranking" label="Ranking" value={form.ranking} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="gpa" label="GPA" value={form.gpa} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="acceptance" label="Acceptance (%)" value={form.acceptance} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
								</Row>

								<h6 className="text-muted mb-2 mt-3">Distribution (%)</h6>
								<Row>
									<NumField name="0_10" label="0–10%" value={form['0_10']} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="1_11" label="1–11%" value={form['1_11']} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="26_50" label="26–50%" value={form['26_50']} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="51_75" label="51–75%" value={form['51_75']} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="76_100" label="76–100%" value={form['76_100']} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
								</Row>

								<h6 className="text-muted mb-2 mt-3">SAT / ACT</h6>
								<Row>
									<NumField name="low_sat" label="SAT low" value={form.low_sat} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="high_sat" label="SAT high" value={form.high_sat} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="low_act" label="ACT low" value={form.low_act} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField name="high_act" label="ACT high" value={form.high_act} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
								</Row>

								<h6 className="text-muted mb-2 mt-3">Cost &amp; size</h6>
								<Row>
									<NumField name="tuition" label="Tuition" value={form.tuition} onChange={setField} disabled={loading} inputStyle={inputStyle} labelStyle={labelStyle} />
									<NumField
										name="undergraduate_enrollment"
										label="Undergraduate enrollment"
										value={form.undergraduate_enrollment}
										onChange={setField}
										disabled={loading}
										inputStyle={inputStyle}
										labelStyle={labelStyle}
									/>
								</Row>

								<div className="d-flex gap-2 mt-4">
									<Button color="primary" type="submit" disabled={loading}>
										{loading ? 'Saving…' : 'Save'}
									</Button>
									<Button color="secondary" type="button" onClick={cancel} disabled={loading}>
										Cancel
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</Container>
	)
}

export { AdminCollegeEdit2 }
